import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { fetchThemeData } from '../../admin/themeSlice';
import { warnIfUnsupportedBrowser } from '../../utils/browserCheck';
import { toSentenceCase } from '../../utils/sentenceCase';
import '../photobooth/photobooth.css';
import './avatar.css';
import { FRAME_MASK_STYLE } from './ornaments';

const API = process.env.REACT_APP_PHOTOBOOTH_API_URL;
const S3  = process.env.REACT_APP_S3_PATH || '';

// Approximate decoded byte size of a data URL's base64 payload (each base64
// char encodes 6 bits, so ~0.75 bytes/char) — used to catch an oversized
// camera capture BEFORE it's sent, instead of only finding out from a raw
// "413 Request Entity Too Large" after the network round trip.
const estimateBase64Bytes = (dataUrl) => {
  const commaIdx = dataUrl.indexOf(',');
  const base64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
  return Math.ceil(base64.length * 0.75);
};

const parseObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const DAY_KEYS = Array.from({ length: 9 }, (_, i) => `day${i + 1}`);
// Must match the same keys PhotoboothAvatars.jsx (step 2) reads on load.
const CATEGORY_STORAGE_KEY = 'pb_category';
const SKIP_PHOTO_STORAGE_KEY = 'pb_skip_photo';
const GENDER_STORAGE_KEY = 'pb_gender';

const PaletteIcon = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path
      d="M24 4C12.4 4 4 12.6 4 23.5 4 34 12 42 22 42c2.6 0 4-1.6 4-3.6 0-1-.4-1.8-1-2.5-.6-.7-1-1.5-1-2.5 0-2 1.6-3.4 3.6-3.4H32c6.6 0 12-5 12-11.4C44 11 35 4 24 4Z"
      fill="#5b2a86"
    />
    <circle cx="13" cy="23" r="3.6" fill="#f5a623" />
    <circle cx="17" cy="13.5" r="3.6" fill="#e04a86" />
    <circle cx="27.5" cy="10.5" r="3.6" fill="#3fa35a" />
    <circle cx="36.5" cy="16.5" r="3.6" fill="#3f7fd8" />
  </svg>
);

// Matches public/img/frame.png's own aspect ratio (1400x1805 — cropped to
// its actual content, see avatar.css's .frame-photo for why) — the photo
// now fills the whole frame box and is clipped to the frame's true hole
// shape via a CSS mask (frame-mask.png), rather than being inset into a
// smaller rectangle, so the visible crop spans the full frame image's
// proportions. Keep in sync with .frame-photo's aspect-ratio in avatar.css
// whenever frame.png itself is swapped for a differently-sized asset.
const PREVIEW_ASPECT = 1400 / 1805;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1;

// Re-encodes a data URL at progressively smaller size/quality until it's
// under maxBytes, or gives up at a floor (300px / 0.35 quality — still a
// legible headshot, just small) rather than looping forever. Used as a
// last-resort retry
// when a camera capture is still oversized after the normal downscale —
// seen mainly on iPhone, where a capture can come out noticeably larger
// than the same shot on Android for a given SNAPSHOT_MAX_SIDE (iOS's HEIC
// pipeline / ImageCapture.takePhoto() output compresses less efficiently
// at the same pixel dimensions than a typical Android camera capture does)
// — retrying smaller actually gets the upload to succeed instead of just
// failing with a nicer error message.
const shrinkDataUrlUntilUnderLimit = (dataUrl, maxBytes) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    let side = Math.max(img.width, img.height);
    let quality = 0.8;

    const attempt = () => {
      const scale = Math.min(1, side / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const out = canvas.toDataURL('image/jpeg', quality);

      if (estimateBase64Bytes(out) <= maxBytes || (side <= 300 && quality <= 0.35)) {
        resolve(out);
        return;
      }
      side = Math.round(side * 0.8);
      quality = Math.max(0.35, quality - 0.1);
      attempt();
    };
    attempt();
  };
  img.onerror = () => reject(new Error('Could not read image'));
  img.src = dataUrl;
});

const cropToZoom = (dataUrl, zoom) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const w = img.width;
    const h = img.height;
    let cw = w;
    let ch = h;
    if (w / h > PREVIEW_ASPECT) cw = h * PREVIEW_ASPECT; else ch = w / PREVIEW_ASPECT;
    cw /= zoom;
    ch /= zoom;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(cw);
    canvas.height = Math.round(ch);
    canvas.getContext('2d').drawImage(img, (w - cw) / 2, (h - ch) / 2, cw, ch, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL('image/jpeg', 0.9));
  };
  img.onerror = () => reject(new Error('Could not read image'));
  img.src = dataUrl;
});

// "Day 1 - 11/10/2026" → ["Day 1", "11/10/2026"] so the date sits on its own
// line under each swatch, as in the reference design.
const splitDayLabel = (label) => {
  const [name, ...rest] = String(label).split(/\s+[-–]\s+/);
  return [name, rest.join(' - ')];
};

/**
 * Step 1 of the Navratri photobooth game, matching navrand-old/welcome.php:
 * pick a day/color, take a selfie or upload a picture, submit — then the
 * avatar/face-swap step (mirrors old main.php) happens on the next page.
 */
export default function Avatar() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate             = useNavigate();
  const dispatch              = useDispatch();

  const [category,      setCategory]      = useState('');
  const [checkingEntry, setCheckingEntry] = useState(true);
  const [gender,        setGender]        = useState('');

  const [preview,      setPreview]      = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cameraActive, setCamera]       = useState(false);
  // Which picker card actually produced the current preview — the camera
  // stays live in the background after a capture (see attachVideoStream
  // below), so `preview` alone can't tell "Upload photo" and "Take a
  // selfie" apart; without this both cards show selected at once.
  const [photoSource,  setPhotoSource]  = useState(null); // 'upload' | 'camera' | null
  const [saving,        setSaving]      = useState(false);
  const [zoom,          setZoom]        = useState(ZOOM_MIN);
  // "Choose a ready animated avatar" — skips the photo step entirely and
  // ships the chosen avatar as-is (face swap happens on the next page).
  const [useAnimatedAvatar, setUseAnimatedAvatar] = useState(false);

  const videoRef      = useRef(null);
  const streamRef      = useRef(null);
  const fileInputRef    = useRef(null);

  // Day-wise toggles — the old PHP game's admin-controlled day1..day9
  // switches. Keys not present in day_toggles default to enabled (a brand
  // new theme with no toggles saved yet shouldn't start with everything
  // hidden).
  const dayToggles   = parseObject(themeData?.day_toggles);
  const dayLabels    = parseObject(themeData?.day_labels);
  const dayColors    = parseObject(themeData?.day_colors);
  const characterTheme = parseObject(themeData?.character_theme);
  const uploadsOpen   = dayToggles.uploads !== false;
  const allowSelfie   = dayToggles.allow_selfie !== false;
  const multipleEntry = dayToggles.multiple_entry !== false;
  // Admin's numeric "Entry limit per user" (Settings tab) — only meaningful
  // when multiple_entry is on; blank/0 means unlimited.
  const entryLimit = multipleEntry ? (parseInt(dayToggles.entry_limit, 10) || 0) : 0;
  const navratriEnabled  = dayToggles.navratri_ai !== false;
  // Gender field — toggleable from the admin's Game Toggles (Settings tab).
  const allowGender  = dayToggles.allow_gender !== false;

  // Only days the admin has turned on show up here — the bonus Superhero
  // bucket is never offered as a "day" on this page.
  const categoryOptions = navratriEnabled
    ? DAY_KEYS.filter(k => dayToggles[k] !== false && Array.isArray(characterTheme[k]) && characterTheme[k].length > 0)
    : [];

  const getDayLabel = (dayKey) => dayLabels[dayKey] || `Day ${dayKey.replace('day', '')}`;
  const getDayColor = (dayKey) => dayColors[dayKey] || '#cccccc';

  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    warnIfUnsupportedBrowser(Swal);
  }, []);

  useEffect(() => {
    setZoom(ZOOM_MIN);
  }, [preview]);

  // multiple_entry off — mirrors old welcome.php hiding the whole form for a
  // user who already has a finished entry; send them straight to the result.
  // This is a genuinely global, one-shot check (not per day/category), so it
  // still runs once on load. The numeric entry_limit case (multiple_entry on)
  // used to also run here as one global count across every day/category —
  // that was the bug: submitting Day 7 twice would count toward Day 1's
  // limit too. That check now happens per-category, right before Submit
  // (see handleSave), instead of once globally on page load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.userId || multipleEntry) {
        setCheckingEntry(false);
        return;
      }
      try {
        const res = await axios.post(
          `${API}/api/photobooth/final`,
          { userId: user.userId },
          { headers: authHeader() },
        );
        if (!cancelled && res.data?.finalImage) {
          navigate('/photobooth/final');
          return;
        }
      } catch {
        // No prior entry (404) or a transient error — let them play.
      }
      if (!cancelled) setCheckingEntry(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, multipleEntry]);

  const galleryEnabled = themeData?.gallery_enabled !== false;

  const authHeader = () => ({
    Authorization: `Bearer ${user?.sessionId}&${user?.organizationId}`,
  });

  // ── Camera ──────────────────────────────────────────────────────────────
  const startCamera = async () => {
    if (cameraActive && streamRef.current) {
      // A stream is already live (e.g. the user re-clicks "Take a selfie"
      // after retaking a shot) — drop back to the live view instead of
      // requesting the camera again, which would leak the old stream and
      // can re-trigger the permission prompt on some browsers.
      setPreview(null);
      setSelectedFile(null);
      setPhotoSource(null);
      setUseAnimatedAvatar(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      // getUserMedia is unavailable on insecure (non-HTTPS/non-localhost)
      // origins and on some older/embedded browsers — the generic camera
      // error below would be misleading here since no permission prompt
      // ever had a chance to appear.
      Swal.fire(
        'Camera Unavailable',
        'Camera access isn’t supported in this browser. Please use the upload option instead.',
        'error',
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1920 }, facingMode: 'user' },
      });
      streamRef.current = stream;
      setPreview(null);
      setSelectedFile(null);
      setPhotoSource(null);
      setUseAnimatedAvatar(false);
      setCamera(true);
    } catch {
      Swal.fire(
        'Camera Error',
        'Unable to access camera. Please allow camera access or use the upload option.',
        'error',
      );
    }
  };

  // Attach the live stream to the <video> element as soon as it mounts, via
  // a callback ref rather than a `[cameraActive]` effect — the video element
  // now unmounts/remounts every time the user retakes a shot (it's swapped
  // out for the captured-photo preview and back), and an effect keyed only
  // on cameraActive wouldn't re-fire on that remount, leaving the new video
  // node with no stream attached. Calling .play() here happens synchronously
  // inside the click handler that mounted this node, which is what Safari
  // (especially iOS) needs to allow autoplay — attaching outside that
  // user-activation window is what silently blocks playback and leaves
  // videoWidth/videoHeight at 0, producing a blank "captured" photo.
  const attachVideoStream = (node) => {
    videoRef.current = node;
    if (!node || !streamRef.current) return;
    node.srcObject = streamRef.current;
    node.play().catch(() => {
      // Autoplay can still be rejected in rare cases (e.g. Low Power Mode) —
      // takeSnapshot() below guards against a video with no frames yet
      // rather than silently capturing a blank image.
    });
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCamera(false);
  };

  // Mobile front cameras happily deliver the full ideal 1920x1920 requested
  // below, while desktop webcams usually cap out much lower — an uncapped
  // snapshot at that resolution can push the base64 payload past prod's
  // upload size limit (Lambda Function URLs / API Gateway both cap
  // synchronous payloads in the single-digit MB range), which is why this
  // only broke on mobile. Downscale before encoding so the payload stays
  // small regardless of the source camera's resolution — plenty of detail
  // survives for face detection either way.
  const SNAPSHOT_MAX_SIDE = 1280;

  // AWS Lambda's hard synchronous-invocation payload cap is 6 MB — a fixed
  // AWS platform limit with no config knob to raise it, regardless of what
  // API Gateway itself allows (10 MB) or what Flask/anything downstream is
  // configured for. API Gateway also base64-encodes the whole request body
  // before handing it to Lambda, inflating whatever we send by ~33%. Plus
  // there's the multipart/form-data overhead itself (boundaries + the
  // other text fields on this form) on top of the image bytes. 3.5 MB
  // (well under the naive 6MB / 1.33 ≈ 4.5MB math) leaves real headroom for
  // all of that instead of sizing right up to the theoretical edge.
  const MAX_UPLOAD_BYTES = 3.5 * 1024 * 1024;

  const captureViaCanvas = (video) => {
    const rawW = video.videoWidth;
    const rawH = video.videoHeight;

    // iOS Safari (and iOS Chrome/Edge, which are all WebKit under the hood)
    // can hand back a getUserMedia stream whose raw frame is landscape
    // while the phone is held upright — the live <video> preview still
    // looks correctly portrait because the browser rotates it for display,
    // but that rotation never touches the underlying pixel buffer. Drawing
    // the raw frame straight to canvas then produces a sideways photo that
    // still renders fine as a preview <img> (so nothing looks broken to the
    // user) but fails face detection on the backend — the "gets rotated"
    // symptom. Detect the mismatch by comparing the video's raw aspect
    // ratio against how it's actually being displayed on screen, and rotate
    // the capture to match what the user saw.
    const displayIsPortrait = video.clientHeight >= video.clientWidth;
    const rawIsPortrait     = rawH >= rawW;
    const needsRotation     = displayIsPortrait !== rawIsPortrait;

    const scale  = Math.min(1, SNAPSHOT_MAX_SIDE / Math.max(rawW, rawH));
    const scaledW = Math.round(rawW * scale);
    const scaledH = Math.round(rawH * scale);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (needsRotation) {
      canvas.width  = scaledH;
      canvas.height = scaledW;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(90 * Math.PI / 180);
      ctx.drawImage(video, -scaledW / 2, -scaledH / 2, scaledW, scaledH);
    } else {
      canvas.width  = scaledW;
      canvas.height = scaledH;
      ctx.drawImage(video, 0, 0, scaledW, scaledH);
    }

    return canvas.toDataURL('image/jpeg', 0.85);
  };

  const takeSnapshot = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.videoWidth || !video.videoHeight) {
      // The stream hasn't actually started rendering frames yet (seen on
      // iOS Safari when autoplay gets delayed) — capturing now would
      // silently produce a blank image that fails later with a confusing
      // "something went wrong" error, so bail out with a clear message and
      // let the user try again once the live preview is actually visible.
      Swal.fire('Camera not ready', 'Please wait a moment for the camera preview to load, then try again.', 'warning');
      return;
    }

    // Prefer the browser's own photo-capture pipeline (ImageCapture) over
    // drawing the raw video frame to a canvas. iOS Safari's getUserMedia
    // stream can carry orientation metadata that the live <video> preview
    // respects (so it looks correctly oriented to the user) but that a raw
    // canvas drawImage() ignores — producing a sideways photo that still
    // renders fine as a preview image but then fails face detection on the
    // backend. ImageCapture.takePhoto() goes through the same pipeline the
    // native camera app uses, so it comes out correctly oriented.
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (track && typeof window.ImageCapture === 'function') {
      try {
        const capture = new window.ImageCapture(track);
        const blob = await capture.takePhoto();
        const dataUrl = await downscaleImageFile(blob);
        setPreview(dataUrl);
        setSelectedFile({ type: 'base64', data: dataUrl });
        setPhotoSource('camera');
        // Deliberately not stopping the camera here — the stream stays live
        // in the background so Retake can drop straight back into the live
        // preview without re-requesting camera permission (see the preview
        // ternary below and attachVideoStream above).
        return;
      } catch (err) {
        console.warn('ImageCapture failed, falling back to canvas capture', err);
      }
    }

    const dataUrl = captureViaCanvas(video);
    setPreview(dataUrl);
    setSelectedFile({ type: 'base64', data: dataUrl });
    setPhotoSource('camera');
    // Camera stays live — see the ImageCapture branch above.
  };

  // ── File input ──────────────────────────────────────────────────────────
  // Same payload-size concern as the live camera snapshot above — a phone's
  // native camera picked through this file dialog can produce a multi-MB
  // photo, so downscale it the same way before sending instead of shipping
  // the raw file.
  const downscaleImageFile = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, SNAPSHOT_MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read image'));
    };
    img.src = objectUrl;
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png'].includes(ext)) {
      Swal.fire('Invalid file', 'Please select a JPG or PNG image.', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire('File too large!', 'Please upload an image smaller than 10 MB.', 'error');
      return;
    }
    stopCamera();
    setUseAnimatedAvatar(false);
    setPhotoSource('upload');
    try {
      const dataUrl = await downscaleImageFile(file);
      setSelectedFile({ type: 'base64', data: dataUrl });
      setPreview(dataUrl);
    } catch {
      // The canvas decode above can fail on some photos even though the
      // <10MB check passed — certain iPhone HEIC/Live-Photo variants in
      // particular. It used to silently fall back to uploading the raw,
      // undownscaled file here, which is exactly what could exceed
      // MAX_UPLOAD_BYTES once API Gateway's base64 wrapping inflates it past
      // Lambda's 6MB limit, surfacing as a confusing "413 Request Entity Too
      // Large" only on the devices where the decode happens to fail (mainly
      // iOS Safari — Android/Chrome rarely hits this path at all). Only take
      // that fallback when the raw file is actually small enough to be safe;
      // otherwise fail with a clear message instead of guaranteeing a 413.
      if (file.size > MAX_UPLOAD_BYTES) {
        Swal.fire(
          'Photo could not be processed',
          "We couldn't process that photo. Please try a different photo or take a selfie instead.",
          'error',
        );
        return;
      }
      setSelectedFile({ type: 'file', data: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const clearPhoto = () => {
    setPreview(null);
    setSelectedFile(null);
    setPhotoSource(null);
  };

  const chooseAnimatedAvatar = () => {
    stopCamera();
    setPreview(null);
    setSelectedFile(null);
    setPhotoSource(null);
    setUseAnimatedAvatar(true);
  };

  const handleShowRules = () => {
    const rulesHtml = rules.length
      ? `<ul class="av-rules-popup-list">${rules.map(rule => `<li>${toSentenceCase(rule)}</li>`).join('')}</ul>`
      : '<p>No rules available for this theme.</p>';

    Swal.fire({
      title: toSentenceCase(themeData?.main_title) || 'How to play',
      html: rulesHtml,
      confirmButtonText: 'Got it',
      confirmButtonColor: themeData?.button_color || '#2f6fed',
      customClass: { popup: 'av-rules-popup' },
      didOpen: (popupEl) => {
        popupEl.style.setProperty('--rules-text-color', themeData?.text_color || '#1a1a2e');
        // Checkmark badge — same button_color/button_text_color pair used by
        // every button across the app (Submit, Gallery, "Got it" itself),
        // instead of the hardcoded blue.
        popupEl.style.setProperty('--rules-badge-bg', themeData?.button_color || '#2f6fed');
        popupEl.style.setProperty('--rules-badge-text', themeData?.button_text_color || '#fff');
      },
    });
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (allowGender && !gender) {
      Swal.fire('Gender required', 'Please select a gender.', 'warning');
      return;
    }
    if (!useAnimatedAvatar && !selectedFile) {
      Swal.fire('No photo', 'Please take a selfie or upload a picture first.', 'warning');
      return;
    }
    if (categoryOptions.length > 0 && !category) {
      Swal.fire('Day required', 'Please select a day from the list.', 'warning');
      return;
    }

    // The admin's numeric entry_limit applies per day/category, not as one
    // global count across the whole event — e.g. a limit of 2 lets a player
    // submit twice for Day 7 *and* twice for Day 1, independently. Checked
    // live here (against the specific category just picked) rather than
    // once on page load, since the category isn't known until now.
    if (multipleEntry && entryLimit) {
      try {
        const res = await axios.post(
          `${API}/api/photobooth/entrycount`,
          { userId: user.userId, category },
          { headers: authHeader() },
        );
        if ((res.data?.count || 0) >= entryLimit) {
          Swal.fire(
            'Limit reached',
            category
              ? `You have reached the maximum number of entries allowed for ${getDayLabel(category)}.`
              : 'You have reached the maximum number of entries allowed.',
            'warning',
          );
          return;
        }
      } catch {
        // Transient error — don't block submission over a failed pre-check;
        // the entry still lands in the same table either way.
      }
    }

    setSaving(true);
    try {
      const form = new FormData();
      form.append('userId', user.userId);
      form.append('name',   user.name || '');
      form.append('email',  user.email || '');
      form.append('userType', user.role || '');
      if (allowGender && gender) form.append('gender', gender);
      if (category) form.append('category', category);
      if (useAnimatedAvatar) {
        form.append('skipPhoto', 'true');
      } else if (selectedFile.type === 'base64') {
        let image = zoom > ZOOM_MIN ? await cropToZoom(selectedFile.data, zoom) : selectedFile.data;
        // Camera captures are already downscaled (see takeSnapshot/
        // captureViaCanvas), but that downscale can still come out
        // oversized on some devices — notably iPhone, where a capture at
        // the same SNAPSHOT_MAX_SIDE compresses less efficiently than the
        // equivalent Android shot and was quietly exceeding this limit,
        // surfacing as a "413 Request Entity Too Large" only there. Retry
        // with progressively smaller size/quality (shrinkDataUrlUntilUnderLimit)
        // instead of just failing — this is what actually makes it work on
        // iPhone the same way it already does on Android, not just a nicer
        // error message when it doesn't.
        if (estimateBase64Bytes(image) > MAX_UPLOAD_BYTES) {
          try {
            image = await shrinkDataUrlUntilUnderLimit(image, MAX_UPLOAD_BYTES);
          } catch {
            // Fall through to the size check below with the original
            // image — the explicit failure message is clearer than a
            // silent guess if even re-encoding it fails.
          }
        }
        if (estimateBase64Bytes(image) > MAX_UPLOAD_BYTES) {
          setSaving(false);
          Swal.fire(
            'Photo could not be processed',
            "That photo is too large to upload. Please retake the selfie or try uploading a photo instead.",
            'error',
          );
          return;
        }
        form.append('image', image);
      } else {
        form.append('file', selectedFile.data);
      }
      await axios.post(`${API}/api/photobooth/upload`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });

      try {
        sessionStorage.setItem(CATEGORY_STORAGE_KEY, category || '');
        sessionStorage.setItem(SKIP_PHOTO_STORAGE_KEY, useAnimatedAvatar ? 'true' : '');
        sessionStorage.setItem(GENDER_STORAGE_KEY, allowGender ? gender : '');
      } catch {
        // sessionStorage can throw in some locked-down browser contexts —
        // the avatars page falls back gracefully if this didn't persist.
      }

      navigate('/photobooth/avatars', {
        state: { category, skipPhoto: useAnimatedAvatar, gender: allowGender ? gender : '' },
      });
    } catch (err) {
      // err.response is only set once a response actually comes back — a
      // request that never completed (network drop, CORS, timeout) leaves
      // it undefined, which used to fall through to the same unhelpful
      // "Please try again." for every failure mode. Distinguishing them
      // makes it possible to tell a real backend rejection apart from a
      // connectivity problem when debugging reports from a device we can't
      // reproduce on directly.
      //
      // "too large" gets its own friendly message rather than showing API
      // Gateway's raw "413 Request Entity Too Large: The data value
      // transmitted exceeds the capacity limit." verbatim — the proactive
      // size check above should catch this before it ever reaches the
      // network, but this is a safety net for anything that slips past it.
      // Matched on the MESSAGE TEXT, not err.response?.status === 413 —
      // depending on which layer (API Gateway vs. a proxy in front of it)
      // actually rejects an oversized request, the real HTTP status code
      // that reaches axios isn't always literally 413 even though the body
      // says so, which was letting this raw text through on some devices.
      const rawMessage = err.response?.data?.error || err.message || '';
      const isTooLarge = err.response?.status === 413 || /too large|entity too large/i.test(rawMessage);
      const message = isTooLarge
        ? "That photo is too large to upload. Please retake the selfie or try uploading a photo instead."
        : rawMessage
          || (err.request ? 'No response from server — please check your connection and try again.' : '')
          || 'Please try again.';
      Swal.fire('Something went wrong', message, 'error');
    } finally {
      setSaving(false);
    }
  };


  if (!themeData || checkingEntry) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  const isMobile = window.innerWidth <= 768;
  const bgImg    = isMobile ? themeData.background_mob : themeData.background_desk;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${S3}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  const btnStyle = {
    background: themeData?.button_background
      ? themeData.button_background
      : `linear-gradient(180deg, ${themeData?.button_color || '#fb9905'}, ${themeData?.button_color || '#f97316'})`,
    color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
  };

  return (
    <div className="av-page" style={bgStyle}>
      <div className="av-wrap">
        {galleryEnabled && (
          <div className="av-nav-tab-row">
            <button type="button" className="av-nav-tab" style={btnStyle} onClick={() => navigate('/gallery')}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
                <rect x="3" y="4" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="8" cy="10" r="1.6" fill="currentColor" />
                <path d="M4 17l5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Gallery
            </button>
          </div>
        )}
        <>
          {/* ── Heading ribbon — admin's "Heading main" image (Images tab)
              takes over the banner slot when uploaded (overlapping the card
              below, same as Kpop's Avatar.jsx). The "?" rules button now
              lives pinned to the card's own top-right corner instead (see
              .av-card-help-btn below), so it no longer depends on this
              image being set. ── */}
          {themeData?.headingMainImage && (
            <div className="av-ribbon-wrap">
              <img
                src={S3 + themeData.headingMainImage}
                className="av-ribbon-img"
                alt="Heading"
              />
            </div>
          )}

          <div className="av-card">
            <button
              type="button"
              className="av-card-help-btn"
              style={btnStyle}
              onClick={handleShowRules}
              aria-label="View rules"
              title="View rules"
            >
              ?
            </button>
          <fieldset disabled={!uploadsOpen} className="av-card-fieldset">
          {/* ── Page heading — admin's Rules-tab "Heading" text (follow_text),
              fetched dynamically and shown at the top of the card. ── */}
          {themeData?.follow_text && (
            <div className="av-page-heading">
              <img src="/img/main-flower.png" alt="" className="av-heading-flourish" aria-hidden="true" />
              <span className="av-page-heading-text">{toSentenceCase(themeData.follow_text)}</span>
              <img src="/img/main-flower.png" alt="" className="av-heading-flourish av-heading-flourish-r" aria-hidden="true" />
            </div>
          )}
          {allowGender && (
            <div className="av-field">
              <label className="av-label" style={{ color: themeData?.text_color }}>Gender</label>
              <select
                className="av-input av-select"
                style={{ color: themeData?.text_color }}
                value={gender}
                onChange={e => setGender(e.target.value)}
              >
                <option value="" disabled>Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          )}

          {/* ── Photo picker: Upload / Selfie / Choose Avatar ────────────── */}
          <div className="av-field">
            <div className={`av-photo-picker-row${preview || cameraActive || useAnimatedAvatar ? ' av-photo-picker-row-compact' : ''}`}>
              <button
                type="button"
                className={`av-photo-picker-card av-photo-picker-card-pink${photoSource === 'upload' && !useAnimatedAvatar && preview ? ' av-photo-picker-card-selected' : ''}`}
                onClick={() => { stopCamera(); fileInputRef.current?.click(); }}
              >
                {photoSource === 'upload' && !useAnimatedAvatar && preview && <span className="av-photo-picker-check">✓</span>}
                <span className="av-photo-picker-icon av-photo-picker-icon-upload" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                    <path
                      d="M7 18a4.5 4.5 0 0 1-.4-8.98A5.5 5.5 0 0 1 17.3 8.1 4 4 0 0 1 17 16H7Z"
                      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
                    />
                    <path d="M12 11v6M9.5 13.5 12 11l2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="av-photo-picker-text">
                  <span className="av-photo-picker-title">Upload photo</span>
                  <span className="av-photo-picker-sub">Upload from device</span>
                </span>
                <span className="av-photo-picker-chevron" aria-hidden="true">›</span>
              </button>

              {allowSelfie && (
              <button
                type="button"
                className={`av-photo-picker-card av-photo-picker-card-blue${cameraActive ? ' av-photo-picker-card-selected' : ''}`}
                onClick={startCamera}
              >
                {cameraActive && <span className="av-photo-picker-check">✓</span>}
                <span className="av-photo-picker-icon av-photo-picker-icon-camera" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                    <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1-1.5h7l1 1.5h2A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                </span>
                <span className="av-photo-picker-text">
                  <span className="av-photo-picker-title">Take a selfie</span>
                  <span className="av-photo-picker-sub">Use your camera</span>
                </span>
                <span className="av-photo-picker-chevron" aria-hidden="true">›</span>
              </button>
              )}

              <button
                type="button"
                className={`av-photo-picker-card av-photo-picker-card-green${useAnimatedAvatar ? ' av-photo-picker-card-selected' : ''}`}
                onClick={chooseAnimatedAvatar}
              >
                {useAnimatedAvatar && <span className="av-photo-picker-check">✓</span>}
                <span className="av-photo-picker-icon av-photo-picker-icon-avatar" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
                    <circle cx="9" cy="10.5" r="1" fill="currentColor" />
                    <circle cx="15" cy="10.5" r="1" fill="currentColor" />
                    <path d="M8.5 14.5c1 1 2.2 1.5 3.5 1.5s2.5-.5 3.5-1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="av-photo-picker-text">
                  <span className="av-photo-picker-title">Choose avatar</span>
                  <span className="av-photo-picker-sub">Pick an animated avatar</span>
                </span>
                <span className="av-photo-picker-chevron" aria-hidden="true">›</span>
              </button>
            </div>

            {!(preview || cameraActive || useAnimatedAvatar) && (
              <>
                <div className="av-picker-divider" aria-hidden="true" />

                <div className="av-hint av-photo-picker-hint" style={{ color: themeData?.text_color }}>
                  {toSentenceCase(themeData?.upload_instruction_text)
                    || 'Upload or take a photo — or skip the photo entirely with a ready animated avatar.'}
                </div>
                {/* handleFileChange below actually enforces this same 10MB
                    cap — this just tells the player upfront instead of
                    letting them find out via the error popup after picking
                    an oversized file. (Raised from 5MB — plenty of
                    legitimate iPhone HEIC photos land between 5-10MB raw,
                    and get downscaled well under the actual upload limit
                    once selected anyway; the old 5MB gate was rejecting
                    those before ever giving them a chance to shrink.) */}
                <div className="av-hint av-photo-size-hint" style={{ color: themeData?.text_color }}>Max photo size: 10 MB</div>
              </>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {/* ── Preview — right below the picker row ──────────────────── */}
            <div className="av-photo-row">
              {useAnimatedAvatar ? (
                <div className="av-photo-preview">
                  <div className="av-animated-avatar-note" style={{ color: themeData?.text_color }}>
                    {toSentenceCase(themeData?.animated_avatar_text)
                      || "Using a ready animated avatar — no photo needed. You'll pick it on the next step."}
                  </div>
                </div>
              ) : preview ? (
                <div className="av-photo-preview">
                  <div className="frame-photo">
                    <div className="frame-photo-window" style={FRAME_MASK_STYLE}>
                      <img
                        src={preview}
                        alt="Selfie preview"
                        className="av-photo-zoom-img"
                        style={{ transform: `scale(${zoom})` }}
                      />
                    </div>
                    <img src="/img/frame.png" alt="" className="frame-photo-overlay" />
                  </div>
                  {selectedFile?.type === 'base64' && (
                    <div className="av-zoom-control">
                      <button
                        type="button"
                        className="av-zoom-btn"
                        aria-label="Zoom out"
                        onClick={() => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP * 2).toFixed(2)))}
                      >
                        −
                      </button>
                      <input
                        type="range"
                        className="av-zoom-slider"
                        min={ZOOM_MIN}
                        max={ZOOM_MAX}
                        step={ZOOM_STEP}
                        value={zoom}
                        aria-label="Zoom"
                        style={{ '--zoom-pct': `${((zoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)) * 100}%` }}
                        onChange={e => setZoom(parseFloat(e.target.value))}
                      />
                      <button
                        type="button"
                        className="av-zoom-btn"
                        aria-label="Zoom in"
                        onClick={() => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP * 2).toFixed(2)))}
                      >
                        +
                      </button>
                    </div>
                  )}
                  <div className="av-photo-actions">
                    <button className="av-btn-secondary" onClick={clearPhoto}>Retake</button>
                  </div>
                </div>
              ) : cameraActive ? (
                <div className="av-camera-live">
                  <div className="frame-photo">
                    <div className="frame-photo-window" style={FRAME_MASK_STYLE}>
                      <video ref={attachVideoStream} autoPlay playsInline muted className="av-video" />
                    </div>
                    <img src="/img/frame.png" alt="" className="frame-photo-overlay" />
                  </div>
                  <div className="av-photo-actions">
                    <button className="av-btn-secondary" onClick={stopCamera}>Cancel</button>
                    <button className="av-btn-primary" style={btnStyle} onClick={takeSnapshot}>
                      📸 Capture
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Day / color category — matches welcome.php's "Select a color" ── */}
          {categoryOptions.length > 0 && (
            <div className="av-field">
              <div className="av-color-panel">
                <div className="av-color-panel-intro">
                  <span className="av-color-panel-icon"><PaletteIcon /></span>
                  <div>
                    <div className="av-color-panel-title">Select your color</div>
                    <div className="av-color-panel-sub">
                      Choose the right color for your festive avatar.
                    </div>
                  </div>
                </div>
                <div className="av-color-swatches" role="radiogroup" aria-label="Select your color">
                  {categoryOptions.map(day => {
                    const [dayName, dayDate] = splitDayLabel(getDayLabel(day));
                    return (
                    <button
                      type="button"
                      key={day}
                      role="radio"
                      aria-checked={category === day}
                      className={`av-color-swatch${category === day ? ' av-color-swatch-active' : ''}`}
                      onClick={() => setCategory(day)}
                    >
                      <span className="av-color-swatch-circle" style={{ background: getDayColor(day) }}>
                        {category === day && <span className="av-color-swatch-check">✓</span>}
                      </span>
                      <span className="av-color-swatch-label">{dayName}</span>
                      {dayDate && <span className="av-color-swatch-date">{dayDate}</span>}
                    </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Submit only appears once a photo (or the animated-avatar option)
              has actually been picked — matches old welcome.php's
              #submit_button, hidden until then. */}
          {(selectedFile || useAnimatedAvatar) && (
            <div className="av-save-row">
              <button
                className={`av-save-btn${themeData?.createAvatarButtonImage && !saving ? ' av-save-btn-image' : ''}`}
                // Skip the theme's background/text color for the image
                // button — an inline style would otherwise beat the CSS
                // that strips .av-save-btn-image's background, since
                // inline styles always win over stylesheet rules.
                style={themeData?.createAvatarButtonImage && !saving ? undefined : btnStyle}
                onClick={handleSave}
                disabled={saving}
              >
                {themeData?.createAvatarButtonImage && !saving ? (
                  <img src={S3 + themeData.createAvatarButtonImage} alt="Create avatar" />
                ) : (
                  <>
                    <span className="av-save-btn-orn" aria-hidden="true">✦</span>
                    {saving ? 'Uploading…' : 'Create avatar'}
                    <span className="av-save-btn-orn" aria-hidden="true">✦</span>
                  </>
                )}
              </button>
            </div>
          )}
          </fieldset>
          </div>

          {!uploadsOpen && (
            <div className="av-card av-closed-card">
              <p style={{ margin: 0, textAlign: 'center', fontWeight: 700, color: themeData?.text_color }}>
                {toSentenceCase(themeData?.uploads_closed_text) || 'Uploads disabled'}
              </p>
            </div>
          )}
        </>
      </div>
    </div>
  );
}
